import "./App.css";
import React, { Component, useEffect } from "react";
import { AegisClient, useAuth } from "@nike/aegis-auth-react";
import config from "../config/config.js";
import {
  getLocalDevTokenGroups,
  isLocalOAuthSkipped,
} from "../config/localDevAuth.js";
import { setGlobalHeader } from "../utilities/http";
import { Button, PageSpinner } from "@nike/epic-react-ui";
import Footer from "../components/Footer";
import { Navigate, Route, Routes } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import RoutingPanel from "../components/RoutingPanel";
import HomePage from "../components/HomePage";
import {
  ROUTE_HOME,
  ROUTE_PALLET_GROUP_ASSIGNMENT,
} from "../constants/routes";
import { resolveModifiedBy } from "../utilities/authUser";

const client = new AegisClient({
  qa: !config.isProd,
  ...config.oAuth,
});

function App() {
  if (isLocalOAuthSkipped()) {
    return <LocalDevApp />;
  }

  return (
    <OAuthErrorBoundary>
      <AegisApp />
    </OAuthErrorBoundary>
  );
}

function LocalDevApp() {
  useEffect(() => {
    setGlobalHeader("Authorization", null);
  }, []);

  return (
    <AuthenticatedShell
      adGroup={getLocalDevTokenGroups()}
      onLogout={() => {
        setGlobalHeader("Authorization", null);
        window.location.href = "/";
      }}
      loginLabel="Continue (local dev, no Okta)"
      tokenClaims={null}
    />
  );
}

function AegisApp() {
  const navigate = useNavigate();

  const {
    loginComplete,
    isLoggedIn,
    clearLogin,
    accessToken,
    tokenClaims,
    loginError,
  } = useAuth(client);

  const loggedIn = typeof isLoggedIn === "function" ? isLoggedIn() : isLoggedIn;

  // Set before child effects run (assignment workspace fetch); useEffect alone runs after children mount.
  if (accessToken) {
    setGlobalHeader("Authorization", `Bearer ${accessToken}`);
  }

  useEffect(() => {
    if (accessToken) {
      setGlobalHeader("Authorization", `Bearer ${accessToken}`);
    } else {
      setGlobalHeader("Authorization", null);
    }
  }, [accessToken]);

  if (!loginComplete) {
    return <PageSpinner show={true} />;
  }

  if (loginError && !accessToken) {
    const errorDetail =
      loginError?.errorSummary ||
      loginError?.message ||
      loginError?.error ||
      (typeof loginError === "string" ? loginError : null);

    return (
      <OktaSignInFailed
        errorDetail={errorDetail}
        onRetry={() => client.login()}
      />
    );
  }

  if (!loggedIn || !accessToken) {
    return <LoggedOut onLogin={() => client.login()} />;
  }

  function handleLogout() {
    setGlobalHeader("Authorization", null);
    clearLogin();
    client.logout();
    navigate("/logout");
  }

  return (
    <AuthenticatedShell
      adGroup={tokenClaims?.groups}
      onLogout={handleLogout}
      tokenClaims={tokenClaims}
    />
  );
}

function AuthenticatedShell({ adGroup, onLogout, loginLabel, tokenClaims }) {
  const modifiedBy = resolveModifiedBy(tokenClaims);
  return (
    <div>
      <Routes>
        <Route path="/" element={<Navigate to={ROUTE_HOME} replace />} />
        <Route
          path={ROUTE_HOME}
          element={
            <HomePage handleLogout={onLogout} displayLogout={true} />
          }
        />
        <Route
          path={ROUTE_PALLET_GROUP_ASSIGNMENT}
          element={
            <RoutingPanel
              adGroup={adGroup}
              handleLogout={onLogout}
              displayLogout={true}
              modifiedBy={modifiedBy}
            />
          }
        />
        <Route
          path="/logout"
          element={
            <LoggedOut
              onLogin={() => (window.location.href = "/")}
              loginLabel={loginLabel}
            />
          }
        />
      </Routes>

      <Footer />
    </div>
  );
}

function OktaSignInFailed({ errorDetail, onRetry }) {
  const clientId = config.oAuth?.clientId;

  return (
    <div style={{ margin: "50px", maxWidth: "640px" }}>
      <span className="epic-body-text-1 table-label">Sign-in failed.</span>
      {errorDetail && (
        <p className="epic-body-text-2 table-label" style={{ marginTop: "12px" }}>
          {errorDetail}
        </p>
      )}
      {String(errorDetail).includes("not assigned to the client") && (
        <p className="epic-body-text-2 table-label" style={{ marginTop: "12px" }}>
          Ask your Okta admin to assign your Nike account to the application{" "}
          <strong>{clientId}</strong> and add{" "}
          <code>http://localhost:3000</code> as a sign-in redirect URI. For UI-only
          local work against <code>localhost:8080</code>, copy{" "}
          <code>.env.development.local.example</code> to{" "}
          <code>.env.development.local</code> with{" "}
          <code>REACT_APP_SKIP_OAUTH=true</code> and restart <code>npm start</code>.
        </p>
      )}
      <p style={{ marginTop: "16px" }}>
        <Button onClick={onRetry}>Retry login</Button>
      </p>
    </div>
  );
}

class OAuthErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      const message =
        this.state.error?.message ||
        this.state.error?.errorSummary ||
        String(this.state.error);

      return (
        <OktaSignInFailed
          errorDetail={message}
          onRetry={() => {
            this.setState({ error: null });
            window.location.href = "/";
          }}
        />
      );
    }

    return this.props.children;
  }
}

function LoggedOut({ onLogin, loginLabel = "Login" }) {
  return (
    <div style={{ margin: "50px" }}>
      <div style={{ marginBottom: "20px" }}>
        <span className="epic-body-text-1 table-label">
          You have been logged out from application.
        </span>
        {!isLocalOAuthSkipped() && (
          <p>
            <span className="epic-body-text-1 table-label">
              Close the browser to log out from Okta session.
            </span>
          </p>
        )}
      </div>
      <Button onClick={onLogin}>{loginLabel}</Button>
    </div>
  );
}

export default App;
